import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/** Lets a MenuItem close the popover after it runs its action. */
const MenuContext = React.createContext<{ close: () => void } | null>(null);

type MenuProps = {
  /** The control that opens the menu (a button); rendered via Popover.Trigger. */
  trigger: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  /** Pin the panel to the trigger's width — for select-like filter menus. */
  matchTrigger?: boolean;
  /** Accessible name for the menu list. */
  ariaLabel?: string;
  contentClassName?: string;
};

/** One popover menu — group labels, items, a selected checkmark, separators and
 *  a danger item — wrapping Radix Popover (Escape + outside-click + focus
 *  management come free; arrow/Home/End roving is added here). Backs kebab menus,
 *  period pickers and select-like filter popovers. Supersedes the hand-rolled
 *  `.src-menu` / `.src-period-menu`. */
export function Menu({
  trigger,
  children,
  open,
  onOpenChange,
  defaultOpen,
  align = "start",
  side = "bottom",
  sideOffset = 6,
  matchTrigger,
  ariaLabel,
  contentClassName,
}: MenuProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const controlled = open != null;
  const actualOpen = controlled ? open : internalOpen;
  const setOpen = (o: boolean) => {
    if (!controlled) setInternalOpen(o);
    onOpenChange?.(o);
  };

  // Radix Popover gives Escape/outside-click but not menu arrow-keying; add
  // roving ArrowUp/Down/Home/End over the focusable items.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        '[role="menuitem"],[role="menuitemradio"],[role="menuitemcheckbox"]',
      ),
    ).filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-disabled") !== "true");
    if (items.length === 0) return;
    e.preventDefault();
    const idx = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    else if (e.key === "ArrowDown") next = idx < 0 ? 0 : (idx + 1) % items.length;
    else next = idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length;
    items[next].focus();
  };

  return (
    <PopoverPrimitive.Root open={actualOpen} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        {/* Deliberately NOT `.vb-root` (same reason as Select): its
            `background: var(--vb-bg)` would paint the panel canvas-grey. Tokens
            resolve globally via :root since AITS-485. */}
        <PopoverPrimitive.Content
          role="menu"
          aria-label={ariaLabel}
          align={align}
          side={side}
          sideOffset={sideOffset}
          onKeyDown={onKeyDown}
          className={cn("vb-menu", matchTrigger && "vb-menu--match-trigger", contentClassName)}
        >
          <MenuContext.Provider value={{ close: () => setOpen(false) }}>{children}</MenuContext.Provider>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

type MenuItemProps = {
  /** Single-line label. Use `title`/`subtitle` instead for a two-line item. */
  children?: React.ReactNode;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  selected?: boolean;
  danger?: boolean;
  disabled?: boolean;
  /** `menuitem` (default), or `menuitemradio`/`menuitemcheckbox` for choices. */
  role?: "menuitem" | "menuitemradio" | "menuitemcheckbox";
  onSelect?: () => void;
  /** Close the menu after selecting (default true). */
  closeOnSelect?: boolean;
  className?: string;
};

export function MenuItem({
  children,
  icon,
  title,
  subtitle,
  selected,
  danger,
  disabled,
  role = "menuitem",
  onSelect,
  closeOnSelect = true,
  className,
}: MenuItemProps) {
  const ctx = React.useContext(MenuContext);
  const checkable = role === "menuitemradio" || role === "menuitemcheckbox";
  return (
    <button
      type="button"
      role={role}
      aria-checked={checkable ? !!selected : undefined}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      className={cn(
        "vb-menu__item",
        selected && "vb-menu__item--selected",
        danger && "vb-menu__item--danger",
        className,
      )}
      onClick={() => {
        if (disabled) return;
        onSelect?.();
        if (closeOnSelect) ctx?.close();
      }}
    >
      {icon != null && (
        <span className="vb-menu__ic" aria-hidden="true">
          {icon}
        </span>
      )}
      {title != null ? (
        <span className="vb-menu__text">
          <span className="vb-menu__title">{title}</span>
          {subtitle != null && <span className="vb-menu__sub">{subtitle}</span>}
        </span>
      ) : (
        <span className="vb-menu__label-txt">{children}</span>
      )}
      {selected && (
        <span className="vb-menu__check" aria-hidden="true">
          <Check size={15} strokeWidth={2.5} />
        </span>
      )}
    </button>
  );
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return <div className="vb-menu__label">{children}</div>;
}

export function MenuSeparator() {
  return <div className="vb-menu__sep" role="separator" />;
}

export default Menu;
